// src/admin/pages/employee/employeeService.ts
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, getDoc, query,
  orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { Employee, EmployeeFormData } from '../../../enums/employee';
import { AuditService } from '../audit/auditService';

const COLLECTION = 'employees';

const mapDoc = (id: string, data: any): Employee => ({
  id,
  fullName: data.fullName || '',
  nic: data.nic || '',
  email: data.email || '',
  phone: data.phone || '',
  address: data.address || '',
  jobRole: data.jobRole || 'Other',
  branchId: data.branchId || '',
  branchName: data.branchName || '',
  basicSalary: data.basicSalary || 0,
  commissionRate: data.commissionRate || 0,
  joinDate: data.joinDate || '',
  status: data.status || 'Active',
  bankName: data.bankName || '',
  bankAccountNumber: data.bankAccountNumber || '',
  notes: data.notes || '',
  createdAt: data.createdAt?.toDate?.() || new Date(),
  updatedAt: data.updatedAt?.toDate?.() || new Date(),
  createdBy: data.createdBy || '',
});

export class EmployeeService {
  static async getAll(): Promise<Employee[]> {
    const snap = await getDocs(query(collection(db, COLLECTION), orderBy('fullName', 'asc')));
    return snap.docs.map(d => mapDoc(d.id, d.data()));
  }

  static async create(formData: EmployeeFormData, userId: string, userName: string): Promise<string> {
    const payload = {
      fullName: formData.fullName,
      nic: formData.nic || null,
      email: formData.email || null,
      phone: formData.phone,
      address: formData.address || null,
      jobRole: formData.jobRole,
      branchId: formData.branchId || null,
      basicSalary: parseFloat(formData.basicSalary) || 0,
      commissionRate: parseFloat(formData.commissionRate) || 0,
      joinDate: formData.joinDate,
      status: formData.status || 'Active',
      bankName: formData.bankName || null,
      bankAccountNumber: formData.bankAccountNumber || null,
      notes: formData.notes || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
    };
    const ref = await addDoc(collection(db, COLLECTION), payload);
    await AuditService.log({
      action: 'create', module: 'Employees', entityId: ref.id, entityLabel: formData.fullName,
      description: `Added new employee "${formData.fullName}" (${formData.jobRole})`,
      userId, userName,
    });
    return ref.id;
  }

  static async update(id: string, formData: EmployeeFormData, userId: string, userName: string): Promise<void> {
    const ref = doc(db, COLLECTION, id);
    await updateDoc(ref, {
      fullName: formData.fullName,
      nic: formData.nic || null,
      email: formData.email || null,
      phone: formData.phone,
      address: formData.address || null,
      jobRole: formData.jobRole,
      branchId: formData.branchId || null,
      basicSalary: parseFloat(formData.basicSalary) || 0,
      commissionRate: parseFloat(formData.commissionRate) || 0,
      joinDate: formData.joinDate,
      status: formData.status,
      bankName: formData.bankName || null,
      bankAccountNumber: formData.bankAccountNumber || null,
      notes: formData.notes || null,
      updatedAt: serverTimestamp(),
    });
    await AuditService.log({
      action: 'update', module: 'Employees', entityId: id, entityLabel: formData.fullName,
      description: `Updated employee record for "${formData.fullName}"`,
      userId, userName,
    });
  }

  static async delete(id: string, userId: string, userName: string): Promise<void> {
    const ref = doc(db, COLLECTION, id);
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    await deleteDoc(ref);
    await AuditService.log({
      action: 'delete', module: 'Employees', entityId: id, entityLabel: data.fullName,
      description: `Removed employee "${data.fullName || 'Unknown'}" from the system`,
      userId, userName,
    });
  }
}
